import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import PdfViewer from '../../items/pdf_viewer';
import VideoGallery from '../../items/video_garally';
import VideoModal from '../../items/video_modal';
import {
  URL_BASE,
  MANUAL,
  FLIGHT_MANUAL,
  FULL_PICTURE_VIDEO,
  FULL_PICTURE_VIDEO_THUMNAIL,
  NEIGHBORHOOD_IMPACT_STUDY_VIDEO,
  NEIGHBORHOOD_IMPACT_STUDY_VIDEO_THUMNAIL,
  HUMAN_IMPACT_STUDY_VIDEO,
  HUMAN_IMPACT_STUDY_VIDEO_THUMNAIL,
  SHOOTING_FOR_DETAIL_VIDEO,
  SHOOTING_FOR_DETAIL_VIDEO_THUMNAIL,
  SECONDARY_DISASTER_SHOOTING_VIDEO,
  SECONDARY_DISASTER_SHOOTING_VIDEO_THUMNAIL,
} from '../../const/config';

const manualURL = `${URL_BASE}contents/manual/${MANUAL}`;
const flightManualURL = `${URL_BASE}contents/manual/${FLIGHT_MANUAL}`;
const fullPictureVideoURL = `${URL_BASE}contents/manual/${FULL_PICTURE_VIDEO}`;
const fullPictureVideoThumbnailURL = `${URL_BASE}contents/manual/${FULL_PICTURE_VIDEO_THUMNAIL}`;
const neighborhoodImpactStudyVideoURL = `${URL_BASE}contents/manual/${NEIGHBORHOOD_IMPACT_STUDY_VIDEO}`;
const neighborhoodImpactStudyVideoThumbnailURL = `${URL_BASE}contents/manual/${NEIGHBORHOOD_IMPACT_STUDY_VIDEO_THUMNAIL}`;
const humanImpactStudyVideoURL = `${URL_BASE}contents/manual/${HUMAN_IMPACT_STUDY_VIDEO}`;
const humanImpactStudyVideoThumbnailURL = `${URL_BASE}contents/manual/${HUMAN_IMPACT_STUDY_VIDEO_THUMNAIL}`;
const shootingForDetailVideoURL = `${URL_BASE}contents/manual/${SHOOTING_FOR_DETAIL_VIDEO}`;
const shootingForDetailVideoThumbnailURL = `${URL_BASE}contents/manual/${SHOOTING_FOR_DETAIL_VIDEO_THUMNAIL}`;
const secondaryDisasterShootingVideoURL = `${URL_BASE}contents/manual/${SECONDARY_DISASTER_SHOOTING_VIDEO}`;
const secondaryDisasterShootingVideoThumbnailURL = `${URL_BASE}contents/manual/${SECONDARY_DISASTER_SHOOTING_VIDEO_THUMNAIL}`;

const manuals = [
  {
    name: 'PDF版マニュアル',
    file: manualURL,
    icon: PictureAsPdfIcon,
  },
];

const VideoManuals = [
  {
    title: '１．被災箇所全容の撮影',
    file: fullPictureVideoURL,
    thumbnail: fullPictureVideoThumbnailURL,
  },
  {
    title: '２．被災箇所周辺への影響調査',
    file: neighborhoodImpactStudyVideoURL,
    thumbnail: neighborhoodImpactStudyVideoThumbnailURL,
  },
  {
    title: '３．人家等への影響調査',
    file: humanImpactStudyVideoURL,
    thumbnail: humanImpactStudyVideoThumbnailURL,
  },
  {
    title: '４．被災箇所詳細の撮影',
    file: shootingForDetailVideoURL,
    thumbnail: shootingForDetailVideoThumbnailURL,
  },
  {
    title: '５．二次災害箇所の調査',
    file: secondaryDisasterShootingVideoURL,
    thumbnail: secondaryDisasterShootingVideoThumbnailURL,
  },
];

const ManualPage = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [deviceType, setDeviceType] = useState('desktop');

  useEffect(() => {
    const checkDevice = () => {
      const userAgent = navigator.userAgent || '';

      // スマートフォンの明確な判定
      const isMobile = /Android.*Mobile|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);

      // iPadの明確な判定
      const isIPad = /iPad/i.test(userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

      // Androidタブレット（Mobileを含まない）
      const isAndroidTablet = /Android/i.test(userAgent) && !/Mobile/i.test(userAgent);

      // 小さな画面サイズ（スマホサイズ）
      const isSmallScreen = window.innerWidth <= 768;

      if (isMobile || isIPad || isAndroidTablet || isSmallScreen) {
        setDeviceType('mobile');
      } else {
        setDeviceType('desktop');
      }
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  return (
    <>
      <Box sx={{ width: '100%' }}>
        {/* 見出し */}
        <Typography
          variant="h6"
          sx={{
            backgroundColor: '#006F80',
            color: '#fff',
            px: 2,
            py: 1,
            borderRadius: 1,
            mb: 2,
            textAlign: 'left',
            fontWeight: 'bold',
          }}
        >
          災害時ドローン撮影調査マニュアル
        </Typography>

        {/* PDF & 動画一覧 */}
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 4,
              mb: 4,
            }}
          >
            {/* PDF表示 */}
            {manuals.map((manual) => (
              <PdfViewer
                key={manual.file}
                icon={manual.icon}
                title={manual.name}
                fileUrl={manual.file}
                deviceType={deviceType}
              />
            ))}

            {/* 動画一覧 */}
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <OndemandVideoIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle1">動画版マニュアル</Typography>
              </Box>
              <VideoGallery videos={VideoManuals} onSelect={setSelectedVideo} />
            </Box>
          </Box>
        </Paper>

        {/* 別PDF（縦並び） */}
        <Typography
          variant="h6"
          sx={{
            backgroundColor: '#006F80',
            color: '#fff',
            px: 2,
            py: 1,
            borderRadius: 1,
            mb: 2,
            textAlign: 'left',
            fontWeight: 'bold',
          }}
        >
          無人航空機飛行マニュアル
        </Typography>

        {deviceType === 'mobile' ? (
          <PdfViewer
            fileUrl={flightManualURL}
            deviceType={deviceType}
          />
        ) : (
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <PdfViewer
              fileUrl={flightManualURL}
              deviceType={deviceType}
            />
          </Paper>
        )}
      </Box>

      {/* 動画モーダル */}
      <VideoModal
        open={selectedVideo !== null}
        onClose={() => setSelectedVideo(null)}
        video={selectedVideo !== null ? VideoManuals[selectedVideo] : null}
      />
    </>
  );
};

export default ManualPage;
